import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { isEmptyAdf } from '@confluence/comments-util/entry-points/domUtils.ts';
import { LoadableLazy } from '@confluence/loadable';
import { useRightSidebarContext } from '@confluence/page-layout-context';

export const OBJECT_LEVEL_REMIX_ENTRY_POINT = 'objectSidebar';
export const OBJECT_LEVEL_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';

const remixObjectPagePopupLoader = () =>
	import(/* webpackChunkName: "loadable-RemixObjectPagePopup" */ './RemixObjectPagePopup.tsx');

const RemixObjectPagePopup = LoadableLazy({
	loader: async () => (await remixObjectPagePopupLoader()).RemixObjectPagePopup,
});

const remixButtonContainerStyle = css({
	position: 'relative',
});

const popupTriggerOverlayStyle = css({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
});

const placeholderButtonStyle = css({
	visibility: 'hidden',
});

const i18n = defineMessages({
	remixPageLabel: {
		id: 'object-sidebar-components.remix-page.label',
		defaultMessage: 'Remix page',
		description: 'Label for the button that opens the page-level remix overlay',
	},
	remixPageTooltip: {
		id: 'object-sidebar-components.remix-page.tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the button that opens the object-level remix overlay',
	},
	remixPageEmptyTooltip: {
		id: 'object-sidebar-components.remix-page.empty-tooltip',
		defaultMessage: 'Add content to this page to remix',
		description: 'Tooltip shown when the remix button is disabled because the page has no content',
	},
	remixInProgressTooltip: {
		id: 'object-sidebar-components.remix-page.in-progress-tooltip',
		defaultMessage: 'Remix in progress',
		description:
			'Tooltip shown when the remix button is disabled because a remix is already in progress',
	},
});

const parseAdf = (value: string): DocNode | null => {
	try {
		const parsed = JSON.parse(value);
		if (!parsed || parsed.type !== 'doc') {
			return null;
		}
		return parsed as DocNode;
	} catch {
		return null;
	}
};

export const RemixPanelButton = ({
	contentBodyValue,
	getAdfDocument,
	isEditorEmpty,
	isDisabled = false,
}: {
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
	isEditorEmpty?: boolean;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const experienceTracker = useAIMateExperienceTracker();

	const isRemixStartActive = useRef(false);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [lazyAdfDocument, setLazyAdfDocument] = useState<DocNode | null>(null);
	const [viewOpenAdfDocument, setViewOpenAdfDocument] = useState<DocNode | null>(null);

	const hasContentFromBody = useMemo(() => {
		if (getAdfDocument) {
			return false;
		}
		if (!contentBodyValue) {
			return false;
		}
		const parsed = parseAdf(contentBodyValue);
		return parsed !== null && !isEmptyAdf(parsed);
	}, [contentBodyValue, getAdfDocument]);

	const hasContent = getAdfDocument ? !isEditorEmpty : hasContentFromBody;

	const resolvedAdfDocument = getAdfDocument ? lazyAdfDocument : viewOpenAdfDocument;

	// Tracks whether the popup has ever been activated so the lazy chunk is loaded
	const [hasBeenActivated, setHasBeenActivated] = useState(false);
	const [isLoadingChunk, setIsLoadingChunk] = useState(false);

	// Preload the heavy popup chunk on hover/focus so it's ready by the time the user clicks
	const handlePreload = useCallback(() => {
		void remixObjectPagePopupLoader();
	}, []);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setViewOpenAdfDocument(null);
		setLazyAdfDocument(null);
		isRemixStartActive.current = false;
	}, []);

	const handleClick = useCallback(async () => {
		if (!hasContent || showSmartCreationModal) {
			return;
		}
		if (isModalOpen || isLoadingChunk) {
			closeModal();
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				remixSource: OBJECT_LEVEL_REMIX_SOURCE,
				product: 'confluence',
			},
		});
		isRemixStartActive.current = true;

		// Ensure the popup chunk is loaded before opening — the hover preload
		// means this will typically resolve instantly from cache
		setIsLoadingChunk(true);
		await remixObjectPagePopupLoader();
		setIsLoadingChunk(false);

		if (getAdfDocument) {
			const doc = await getAdfDocument();
			if (doc) {
				setLazyAdfDocument(doc);
				setIsModalOpen(true);
				setHasBeenActivated(true);
			}
		} else if (contentBodyValue) {
			const doc = parseAdf(contentBodyValue);
			if (doc) {
				setViewOpenAdfDocument(doc);
				setIsModalOpen(true);
				setHasBeenActivated(true);
			}
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				source: 'remixModalFromObjectSidebar',
				attributes: {
					entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		experienceTracker,
		hasContent,
		isModalOpen,
		showSmartCreationModal,
		closeModal,
		isLoadingChunk,
		getAdfDocument,
		contentBodyValue,
	]);

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			if (isRemixStartActive.current) {
				experienceTrackerRef.current.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User closed remix overlay',
					attributes: { reason: 'User closed remix overlay' },
				});
				isRemixStartActive.current = false;
			}
		};
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const tooltipContent =
		isModalOpen && !showSmartCreationModal
			? undefined
			: showSmartCreationModal
				? formatMessage(i18n.remixInProgressTooltip)
				: hasContent
					? formatMessage(i18n.remixPageTooltip)
					: formatMessage(i18n.remixPageEmptyTooltip);

	return (
		<div css={remixButtonContainerStyle} onMouseEnter={handlePreload} onFocus={handlePreload}>
			<Tooltip position="left" content={tooltipContent} hideTooltipOnClick>
				<IconButton
					onClick={handleClick}
					appearance="subtle"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					icon={AiGenerativeRemixIcon}
					label={formatMessage(i18n.remixPageLabel)}
					testId="sidebar-remix-button"
					isLoading={isLoadingChunk}
					isDisabled={isDisabled || !hasContent || showSmartCreationModal}
				/>
			</Tooltip>
			{hasBeenActivated && (
				<RemixObjectPagePopup
					adfDocument={resolvedAdfDocument ?? ({ type: 'doc', version: 1, content: [] } as DocNode)}
					isModalOpen={isModalOpen}
					onClose={closeModal}
					onStopThinking={onStopThinking}
					popupRepositionKey={rightSidebarWidth}
					trigger={(triggerProps) => (
						<div css={popupTriggerOverlayStyle}>
							<IconButton
								{...triggerProps}
								css={placeholderButtonStyle}
								appearance="subtle"
								shape={isRovoButtonEnabled ? 'default' : 'circle'}
								icon={AiGenerativeRemixIcon}
								label={formatMessage(i18n.remixPageLabel)}
								testId="sidebar-remix-button"
								isDisabled={isDisabled || !hasContent || showSmartCreationModal}
								isSelected={isModalOpen && !showSmartCreationModal}
								aria-hidden="true"
							/>
						</div>
					)}
				/>
			)}
		</div>
	);
};
